///
/// EarthBrowser
///
/// Created by Matt Giger on 9/16/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Central accounting of GPU texture memory allocated through the engine.
/// Every Texture/TextureCube allocation and free updates these counters, so
/// the app can budget against a device-class limit (Safari/WebKit exposes no
/// memory-pressure or heap APIs, so self-accounting is the only reliable
/// signal on mobile). Estimates assume tightly-packed driver storage; real
/// usage can be somewhat higher due to driver padding.

import { ComponentType, InternalFormat, PixelFormat } from "./GLConstants"

export interface TextureMemoryStats {
	/** Estimated bytes currently allocated in GPU storage: textures plus vertex/index buffers */
	bytes: number
	/** High-water mark of bytes since page load */
	peakBytes: number
	/** Number of live GL texture objects with storage */
	count: number
	/** Texture portion of `bytes` */
	textureBytes: number
	/** Vertex and index buffer portion of `bytes` */
	bufferBytes: number
	/** Number of live GL buffer objects with storage */
	bufferCount: number
}

const stats: TextureMemoryStats = {
	bytes: 0,
	peakBytes: 0,
	count: 0,
	textureBytes: 0,
	bufferBytes: 0,
	bufferCount: 0,
}

/** Snapshot of current GPU memory accounting */
export function textureMemoryStats(): TextureMemoryStats {
	return { ...stats }
}

function updatePeak(): void {
	if(stats.bytes > stats.peakBytes) {
		stats.peakBytes = stats.bytes
	}
}

/**
 * Adjust the global texture memory counters. Called by Texture/TextureCube
 * on every allocation, reallocation and free.
 */
export function adjustTextureMemory(byteDelta: number, countDelta: number): void {
	stats.textureBytes += byteDelta
	stats.bytes += byteDelta
	stats.count += countDelta
	updatePeak()
}

/**
 * Adjust the buffer memory counters. Called by VertexBuffer/IndexBuffer on
 * every upload and free, so mesh storage (relief-promoted tiles run to
 * ~200 KB each) counts against the same budget as textures.
 */
export function adjustBufferMemory(byteDelta: number, countDelta: number): void {
	stats.bufferBytes += byteDelta
	stats.bytes += byteDelta
	stats.bufferCount += countDelta
	updatePeak()
}

function componentBytes(dataType: number): number {
	switch(dataType) {
		case ComponentType.Byte:
		case ComponentType.UnsignedByte:
			return 1
		case ComponentType.Short:
		case ComponentType.UnsignedShort:
			return 2
		case ComponentType.Int:
		case ComponentType.UnsignedInt:
		case ComponentType.Float:
			return 4
		default:
			return 1
	}
}

function bytesPerPixel(internalFormat: number, dataType: number): number {
	switch(internalFormat) {
		case InternalFormat.RGBA:	// unsized: storage depends on the data type
			return 4 * componentBytes(dataType)
		case InternalFormat.RGBA8:
			return 4
		case InternalFormat.RGB8:
			return 3
		case PixelFormat.Luminance:
		case InternalFormat.R8:
		case InternalFormat.R8I:
		case InternalFormat.R8UI:
			return 1
		case InternalFormat.RG8:
		case InternalFormat.R16I:
		case InternalFormat.R16UI:
			return 2
		case InternalFormat.R32F:
		case InternalFormat.R32I:
		case InternalFormat.R32UI:
			return 4
		case InternalFormat.RG32F:
			return 8
		default:
			return 4
	}
}

/**
 * Estimate the GPU bytes for a texture allocation. Mipmapped textures cost
 * an extra ~1/3 of the base level.
 */
export function estimateTextureBytes(
	internalFormat: number,
	dataType: number,
	width: number,
	height: number,
	mipmapped: boolean,
	faces: number = 1
): number {
	const base = width * height * bytesPerPixel(internalFormat, dataType) * faces
	return mipmapped ? Math.ceil(base * 4 / 3) : base
}
